<template>
  <div class="stk-p-8">
    <PageHeading class="stk-mb-6">
      <template v-slot:title> Setup </template>
    </PageHeading>

    <div class="cols">
      <Stack gap="0">
        <div class="row" v-for="link in allLinks">
          <header>
            <Title size="5">{{ link.title }}</Title>
            <Subtitle>{{ link.description }}</Subtitle>
          </header>

          <Button
            :key="link.label"
            :href="link.href"
            :to="link.to"
            @click="link.click && link.click()"
            :icon-before="link.icon"
          >
            {{ link.label }}
          </Button>
        </div>
      </Stack>

      <div class="cards">
        <PeopleCard
          name="Behavioral Questionnaire"
          description="This behavioral questionnaire asks about values
          without overwhelming or offending. Embed the link for new leads, or send to specific prospects."
          :main-link="questionnaireLink"
          :other-links="[
            {
              name: 'Export All Responses',
              url: '/questionnaire/download-all-raw-responses/',
            },
            {
              name: 'Export All Personas',
              url: '/questionnaire/download-personality-categories/',
            },
            {
              name: 'View or Print All Questions',
              url: '/questionnaire/print/',
            },
          ]"
        />

        <PeopleCard
          name="Values Picker"
          description="The values picker provides more customization
          by allowing prospects to select specific categories and values important to them."
          :main-link="wizardLink"
          :other-links="[
            {
              name: 'Customize Values Picker',
              url: '/advisor-dashboard/edit-wizard/',
            },
          ]"
        />

        <PeopleCard
          name="Risk Tolerance Questionnaire"
          description="This questionnaire asks about risk tolerance
          without overwhelming or offending. Embed the link for new
          leads, or send to specific prospects."
          :main-link="`${questionnaireLink}?questionnaire_type=risk`"
          :other-links="[
            {
              name: 'View Methodology',
              url: 'https://assets-global.website-files.com/6307966dd164702a745df6dd/65946c4073193f0183606cb1_Risk%20Tolerance%20Questionnaire%20DEC%2023%20(2).pdf',
            },
          ]"
        />

        <div class="customize">
          <p>You can customize the questionnaire link:</p>
          <form @submit.prevent="submitForm">
            <Buttons gap="1">
              <strong>{{ newLinkPreview }}</strong>
              <TextInput
                v-model="newLinkSlug"
                spacing="tight"
                name="link_slug"
                :maxlength="50"
                :disabled="!initializedFromStore || isSubmitting"
                required
              />
              <Button
                variant="primary"
                spacing="tight"
                :disabled="!initializedFromStore"
                :loading="isSubmitting"
              >
                Change link
              </Button>
            </Buttons>
          </form>
        </div>
      </div>
    </div>

    <StakeModal v-model="showMeetingSettings">
      <MeetingBotSettings />
    </StakeModal>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { ToastProgrammatic as Toast } from 'buefy';
import { Button, Buttons, PageHeading, Stack, StakeModal, Subtitle, TextInput, Title } from "@stakeui";
import { useAdvisorStore } from "@frontend/ui/stores/advisorStore";
import PeopleCard from "@frontend/ui/features/people/PeopleCard.vue";
import MeetingBotSettings from '@frontend/ui/features/meetings/components/MeetingBotSettings.vue';

const advisorStore = useAdvisorStore();
const showMeetingSettings = ref(false);
const isSubmitting = ref(false);
const savedLinkSlug = ref(advisorStore.advisor?.link_slug ?? '');
const newLinkSlug = ref(advisorStore.advisor?.link_slug ?? '');
const initializedFromStore = ref(false);

const canEditLogo = computed(
  () =>
    advisorStore.advisor?.is_firm_admin ||
    advisorStore.advisor?.can_edit_disclosures_logo
);

const newLinkPreview = computed(() => `${window.location.protocol}//${window.location.host}/questionnaire/intro/`);

const allLinks = computed(() => {
  const items = [
    {
      to: { name: "oauth-integration-dashboard" },
      icon: "fas fa-plug",
      label: "Manage Integrations",
      title: "Integrations",
      description: "Connect your CRM and Calendar. View instructions for Custodian integrations.",
    },
  ];

  if (advisorStore.hasFeature('CRMMigration')) {
    items.push({
      to: { name: "crm-mapping" },
      icon: "fas fa-exchange-alt",
      label: "Manage CRM Mapping",
      title: "CRM Field Mapping",
      description: "Generate and edit AI-powered field mappings between your dossier template and CRM system.",
    });
  }

  if (
    advisorStore.hasFeature("Meetings")
  ) {
    items.push({
      click: () => showMeetingSettings.value = true,
      icon: "fas fa-calendar",
      label: "Meeting Settings",
      title: "Meeting Settings",
      description: "Configure Meeting bot appearance, data retention, joining preferences, and more.",
    });
  }

  items.push(
    {
      href: "/advisor-dashboard/values/",
      icon: "fas fa-edit",
      label: "Manage Values Sets",
      title: "Manage Values Sets",
      description: "View and edit your custom values sets.",
    },
  );

  if (
    advisorStore.hasFeature("Portfolio Checkup") &&
    advisorStore.hasFeature("Model Matchmaker")
  ) {
    items.push({
      to: { name: "configure-portfolios" },
      icon: "fas fa-edit",
      label: "Configure",
      title: "Configure Model portfolios",
      description: 'Configure model portfolios for "Load Best Fit" functionality.',
    });
  }

  items.push(
    {
      href: "/datadictionary/",
      icon: "fas fa-book",
      label: "View Dictionary",
      title: "Dictionary",
      description: "View YourStake's Data Dictionary",
    },
  );

  if (canEditLogo.value) {
    items.push(
      {
        href: "/disclosures/",
        icon: "fas fa-file-alt",
        label: "Add Disclosures",
        title: "Disclosures",
        description: "Add and edit your firm's disclosures.",
      },
      {
        href: "/advisordashboard/update-logo/",
        icon: "fas fa-image",
        label: "Update Firm Logo",
        title: "Firm Logo",
        description: "Update your firm logo.",
      }
    );
  }

  return items;
});

const questionnaireLink = computed(() => `${window.location.protocol}//${window.location.host}/questionnaire/intro/${savedLinkSlug.value}/`);
const wizardLink = computed(() => `${window.location.protocol}//${window.location.host}/ui/visitor/wizard?advisor=${savedLinkSlug.value}`);

async function submitForm() {
  if (!initializedFromStore.value || isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    const body = new URLSearchParams();
    body.set('link_slug', newLinkSlug.value);
    body.set('csrfmiddlewaretoken', advisorStore.csrfToken);

    const resp = await fetch('/advisordashboard/update-questionnaire-link/', {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-CSRFToken': advisorStore.csrfToken,
      },
      redirect: 'follow',
    });
    const redirectedToSetup =
      resp.redirected ||
      (resp.url && resp.url.includes('questionnaire-link-updated=true'));

    if (redirectedToSetup) {
      savedLinkSlug.value = newLinkSlug.value;

      Toast.open({
        message: `Link successfully updated.`,
        type: 'is-success',
      });
      return;
    }

    // If no redirect, Django probably re-rendered the form with errors (HTML)
    if (!resp.ok) {
      Toast.open({
        message: `Something went wrong. Please try again.`,
        type: 'is-danger',
      });
    }
  } catch (e) {
    console.error(e);
    Toast.open({
      message: `There was an error updating the link.`,
      type: 'is-danger',
    });
  } finally {
    isSubmitting.value = false;
  }
}

// one-time init when link_slug becomes available
let stop = () => {}; // declare first

// eslint-disable-next-line prefer-const
stop = watch(
    () => advisorStore.advisor,
    advisor => {
      if (!initializedFromStore.value && advisor) {
        const storedSlug = advisor.link_slug ?? advisor.slug;

        if (storedSlug != null) {
          savedLinkSlug.value = storedSlug;
          newLinkSlug.value = storedSlug;
          initializedFromStore.value = true;
          stop(); // stop watching once initialized
        }
      }
    },
    { immediate: true },
);
</script>

<style scoped>
.cols {
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
}

.cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.row {
  border-top: 1px solid var(--stk-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3rem;
  padding: 1em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;

  &:hover {
    background-color: var(--stk-background-alt);
  }

  &:last-child {
    border-bottom: 1px solid var(--stk-border);
  }
}

.customize {
  background-color: var(--stk-background);
  border: 1px solid var(--stk-border);
  padding: 1em;
  border-radius: 1em;
  font-size: 0.875em;
  grid-column: span 3;
}
</style>
